import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Edit3,
  Grid2X2,
  List,
  Plus,
  RefreshCw,
  Save,
  Search,
  X,
} from "lucide-react";
import api from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import Heading from "../../components/ui/Heading";
import { getSessionUsername } from "../../utils/sessionUser";

const today = () => new Date().toISOString().split("T")[0];

const firstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split("T")[0];
};

const currency = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getUserName = (user) => {
  if (!user) return getSessionUsername();
  if (typeof user === "string") return user;
  return user.UsrName || user.UserName || getSessionUsername();
};

const getRowKey = (row, index) =>
  row?.AdjId || row?.DocId || row?.PrimeId || row?.DocNo || row?.vid || index;

const getCustomerVid = (customer) => customer?.vid || customer?.Vid || customer?.VId || "";

const getCustomerLabel = (customer) =>
  [
    customer?.vname || customer?.Vname || customer?.VName,
    customer?.vcode || customer?.VCode || customer?.Vcode,
  ]
    .filter(Boolean)
    .join(" / ");

const getCustomerType = (customer) =>
  String(customer?.VType || customer?.vtype || customer?.VTYPE || "").trim().toLowerCase();

const getPrimeId = (row) =>
  row?.PrimeId || row?.InvAdjId || row?.AdjId || row?.DocId || row?.invAdjId || row?.id || 0;

const toApiNumber = (value) => Number(value || 0);

const getPaymentModeId = (paymentMode) =>
  paymentMode?.bANKID ?? paymentMode?.BANKID ?? paymentMode?.BankId ?? paymentMode?.bankId ?? "";

const getPaymentModeLabel = (paymentMode) =>
  paymentMode?.PAYMODE || paymentMode?.PayMode || paymentMode?.payMode || "";

const normalizeAdjustmentRow = (row) => ({
  ...row,
  vid: row.vid ?? row.Vid ?? row.VId,
  vcode: row.vcode ?? row.VCode ?? row.Vcode,
  vname: row.vname ?? row.VName ?? row.Vname,
  mobile: row.mobile ?? row.Mobile,
  vtype: row.vtype ?? row.VType ?? row.VTYPE,
  opbalance: row.opbalance ?? row.opBalance ?? row.OpBalance,
  cashpayments: row.cashpayments ?? row.CashPayments,
  bankpayments: row.bankpayments ?? row.BankPayments,
  OtherPayments: row.OtherPayments ?? row.otherPayments,
  Balance: row.Balance ?? row.balance,
});

const InvAdjustment = () => {
  const { user } = useAuth();
  const userName = getUserName(user);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const customerInputRef = useRef(null);
  const modeButtonRef = useRef(null);
  const refreshButtonRef = useRef(null);

  const [mode, setMode] = useState("list");
  const [viewMode, setViewMode] = useState("card");
  const [listRows, setListRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingRow, setEditingRow] = useState(null);

  const [docNo, setDocNo] = useState("");
  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(today);
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [highlightedCustomer, setHighlightedCustomer] = useState(0);
  const [selectedVid, setSelectedVid] = useState("");
  const [selectedVids, setSelectedVids] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [selectedVendorType, setSelectedVendorType] = useState("All");
  const [paymentModes, setPaymentModes] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [paymentModeOpen, setPaymentModeOpen] = useState(false);
  const [highlightedPaymentMode, setHighlightedPaymentMode] = useState(0);
  const [adjustmentRows, setAdjustmentRows] = useState([]);
  const [adjAmounts, setAdjAmounts] = useState({});

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setError("");
    try {
      const data = await api.GetinvAdjustment(userName);
      setListRows(data?.Table || []);
    } catch (err) {
      console.error("Failed to load invoice adjustment list:", err);
      setError("Failed to load invoice adjustments.");
      setListRows([]);
    } finally {
      setListLoading(false);
    }
  }, [userName]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredListRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return listRows;

    return listRows.filter((row) =>
      Object.values(row || {})
        .filter((value) => value !== null && value !== undefined)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [listRows, searchTerm]);

  const resetForm = useCallback(() => {
    setDocNo("");
    setFromDate(firstDayOfMonth());
    setToDate(today());
    setCustomers([]);
    setCustomerQuery("");
    setCustomerOpen(false);
    setHighlightedCustomer(0);
    setSelectedVid("");
    setSelectedVids([]);
    setVendorTypes([]);
    setSelectedVendorType("All");
    setPaymentModes([]);
    setSelectedBankId("");
    setPaymentModeOpen(false);
    setHighlightedPaymentMode(0);
    setAdjustmentRows([]);
    setAdjAmounts({});
  }, []);

  const loadFormDefaults = useCallback(async () => {
    setFormLoading(true);
    setError("");
    try {
      const data = await api.GetInvAdjuestDocAPI(userName);
      const partyRows = data?.Table || [];
      const defaultDocNo = data?.Table1?.[0]?.DocNo || "";
      const modeRows = data?.Table2 || [];
      const vendorTypeRows = data?.Table3 || [];
      const selectedValue = String(
        editingRow?.vid ||
          editingRow?.Vid ||
          editingRow?.VId ||
          ""
      );
      const defaultBankId = String(
        editingRow?.bANKID ??
          editingRow?.BANKID ??
          editingRow?.BankId ??
          getPaymentModeId(modeRows?.[0]) ??
          ""
      );
      const selectedCustomer = partyRows.find(
        (customer) => String(getCustomerVid(customer)) === selectedValue
      );

      setCustomers(partyRows);
      setVendorTypes(vendorTypeRows);
      setSelectedVendorType(vendorTypeRows?.[0]?.VType || "All");
      setPaymentModes(modeRows);
      setDocNo(editingRow?.DocNo || editingRow?.docNo || defaultDocNo);
      setSelectedVid(selectedValue);
      setSelectedVids(selectedValue ? [selectedValue] : []);
      setSelectedBankId(defaultBankId);
      setCustomerQuery("");

      if (editingRow) {
        const editData = await api.EditinvAdjustment(userName, getPrimeId(editingRow));
        const editRows = editData?.Table || [];

        if (editRows.length > 0) {
          const firstRow = editRows[0];
          const editVid = String(
            firstRow.vid || firstRow.Vid || firstRow.VId || firstRow.VID || selectedValue
          );
          const editBankId = String(
            firstRow.bANKID ??
              firstRow.BANKID ??
              firstRow.BankId ??
              firstRow.bankId ??
              defaultBankId
          );
          const editCustomer =
            partyRows.find((customer) => String(getCustomerVid(customer)) === editVid) ||
            firstRow;

          setDocNo(firstRow.DocNo || firstRow.docNo || firstRow.Docno || defaultDocNo);
          setSelectedVid(editVid);
          setSelectedVids(editVid ? [editVid] : []);
          setSelectedBankId(editBankId);
          setCustomerQuery("");
          setAdjustmentRows(editRows);
          setAdjAmounts(
            editRows.reduce((acc, row, index) => {
              acc[getRowKey(row, index)] = row.AdjAmt || row.AdjAmount || "";
              return acc;
            }, {})
          );
        }
      }
    } catch (err) {
      console.error("Failed to load invoice adjustment form defaults:", err);
      setError("Failed to load invoice adjustment details.");
      setCustomers([]);
      setPaymentModes([]);
    } finally {
      setFormLoading(false);
    }
  }, [editingRow, userName]);

  useEffect(() => {
    if (mode !== "form") return;
    resetForm();
    loadFormDefaults();
  }, [mode, resetForm, loadFormDefaults]);

  const openNew = () => {
    setEditingRow(null);
    setMode("form");
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setMode("form");
  };

  const closeForm = () => {
    setMode("list");
    setEditingRow(null);
    resetForm();
    fetchList();
  };

  const loadVendorAdjustment = async () => {
    if (selectedVids.length === 0) {
      setError("Please select at least one customer.");
      return;
    }

    setAdjustmentLoading(true);
    setError("");
    try {
      const vendorTypeParam = selectedVendorType || "All";
      const vendorType = String(vendorTypeParam).trim().toLowerCase();
      const results = await Promise.all(
        selectedVids.map((vid) => api.GetVendorSalesAdjustment(fromDate, toDate, vid, vendorTypeParam))
      );
      const rows = results
        .flatMap((data) => data?.Table || [])
        .map(normalizeAdjustmentRow)
        .filter((row) => vendorType === "all" || getCustomerType(row) === vendorType);
      setAdjustmentRows(rows);
      setAdjAmounts(
        rows.reduce((acc, row, index) => {
          acc[getRowKey(row, index)] = row.AdjAmount || "";
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Failed to load vendor sales adjustment:", err);
      setError("Failed to load customer invoice balance.");
      setAdjustmentRows([]);
      setAdjAmounts({});
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    const query = customerQuery.trim().toLowerCase();
    const vendorType = String(selectedVendorType || "All").trim().toLowerCase();
    const typeFilteredCustomers =
      vendorType === "all"
        ? customers
        : customers.filter((customer) => getCustomerType(customer) === vendorType);

    if (!query) return typeFilteredCustomers;

    return typeFilteredCustomers.filter((customer) =>
      [getCustomerLabel(customer), getCustomerVid(customer)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [customers, customerQuery, selectedVendorType]);

  const selectedPaymentMode = useMemo(
    () =>
      paymentModes.find(
        (paymentMode) => String(getPaymentModeId(paymentMode)) === String(selectedBankId)
      ),
    [paymentModes, selectedBankId]
  );

  const toggleCustomer = (customer) => {
    if (!customer) return;
    const vid = String(getCustomerVid(customer));
    setSelectedVids((current) => {
      const exists = current.includes(vid);
      const next = exists ? current.filter((currentVid) => currentVid !== vid) : [...current, vid];
      setSelectedVid(next[0] || "");
      return next;
    });
    setCustomerQuery("");
    setCustomerOpen(true);
    setHighlightedCustomer(0);
  };

  const handleCustomerKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCustomerOpen(true);
      setHighlightedCustomer((current) =>
        Math.min(current + 1, Math.max(filteredCustomers.length - 1, 0))
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedCustomer((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (customerOpen && filteredCustomers.length > 0) {
        toggleCustomer(filteredCustomers[highlightedCustomer]);
      } else {
        setCustomerOpen(true);
      }
      window.setTimeout(() => modeButtonRef.current?.focus(), 0);
      return;
    }

    if (event.key === "Tab") {
      if (filteredCustomers.length > 0) {
        toggleCustomer(filteredCustomers[highlightedCustomer]);
      }
      setCustomerOpen(false);
      window.setTimeout(() => modeButtonRef.current?.focus(), 0);
    }
  };

  const selectPaymentMode = (index) => {
    const paymentMode = paymentModes[index];
    if (!paymentMode) return;
    setSelectedBankId(String(getPaymentModeId(paymentMode)));
    setPaymentModeOpen(false);
    setHighlightedPaymentMode(index);
  };

  const handlePaymentModeKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setPaymentModeOpen(true);
      setHighlightedPaymentMode((current) =>
        Math.min(current + 1, Math.max(paymentModes.length - 1, 0))
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setPaymentModeOpen(true);
      setHighlightedPaymentMode((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (paymentModeOpen) {
        selectPaymentMode(highlightedPaymentMode);
        refreshButtonRef.current?.focus();
      } else {
        setPaymentModeOpen(true);
      }
      return;
    }

    if (event.key === "Tab" && paymentModeOpen) {
      selectPaymentMode(highlightedPaymentMode);
    }
  };

  const saveAdjustment = async () => {
    if (!docNo) {
      setError("DocNo is required.");
      return;
    }
    if (selectedVids.length === 0) {
      setError("Please select at least one customer.");
      return;
    }
    if (!selectedBankId) {
      setError("Please select a mode.");
      return;
    }
    if (adjustmentRows.length === 0) {
      setError("Please refresh and load adjustment rows before saving.");
      return;
    }

    setFormLoading(true);
    setError("");
    try {
      const sqlAction = editingRow ? "Update" : "Insert";
      const selectedMode = paymentModes.find(
        (paymentMode) =>
          String(getPaymentModeId(paymentMode)) ===
          String(selectedBankId)
      );
      const payload = {
        Sp_SqlAction: sqlAction,
        DocNo: docNo,
        UserName: userName,
        BankId: toApiNumber(selectedBankId),
        PaymentMode: getPaymentModeLabel(selectedMode),
        VType: selectedVendorType,
        Items: adjustmentRows.map((row, index) => ({
          Vid: toApiNumber(row.vid || row.Vid || row.VId || selectedVid),
          Vcode: row.vcode || row.VCode || row.Vcode || "",
          VName: row.vname || row.VName || row.Vname || "",
          Mobile: row.mobile || row.Mobile || "",
          opbalance: toApiNumber(row.opbalance || row.OpBalance),
          InvValule: toApiNumber(row.invValule || row.InvValule || row.InvValue),
          CashPayments: toApiNumber(row.cashpayments || row.CashPayments),
          BankPayments: toApiNumber(row.bankpayments || row.BankPayments),
          OtherPayments: toApiNumber(row.OtherPayments || row.otherPayments),
          Balance: toApiNumber(row.Balance || row.balance),
          tag: row.tag || row.Tag || "",
          AdjAmt: toApiNumber(adjAmounts[getRowKey(row, index)]),
        })),
      };

      await api.SaveInvAdjAmount(payload);
      window.alert(editingRow ? "Invoice adjustment updated successfully." : "Invoice adjustment saved successfully.");
      closeForm();
    } catch (err) {
      console.error("Failed to save invoice adjustment:", err);
      setError("Failed to save invoice adjustment.");
    } finally {
      setFormLoading(false);
    }
  };

  const renderExplorerTable = () => {
    const keys = Array.from(
      new Set(filteredListRows.flatMap((row) => Object.keys(row || {})))
    ).slice(0, 8);

    return (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {keys.length > 0 ? (
                  keys.map((key) => (
                    <th key={key} className="px-4 py-3 text-left font-semibold">
                      {key}
                    </th>
                  ))
                ) : (
                  <th className="px-4 py-3 text-left font-semibold">Invoice Adjustment</th>
                )}
                <th className="px-4 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listLoading ? (
                <tr>
                  <td colSpan={Math.max(keys.length + 1, 2)} className="px-4 py-12 text-center font-semibold text-slate-500">
                    Loading invoice adjustments...
                  </td>
                </tr>
              ) : filteredListRows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(keys.length + 1, 2)} className="px-4 py-12 text-center font-semibold text-slate-500">
                    No invoice adjustments found.
                  </td>
                </tr>
              ) : (
                filteredListRows.map((row, index) => (
                  <tr key={getRowKey(row, index)} className="hover:bg-teal-50/60">
                    {keys.map((key) => (
                      <td key={key} className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {row?.[key] ?? "-"}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                      >
                        <Edit3 className="h-4 w-4" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderExplorerCards = () => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {listLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center font-semibold text-slate-500 shadow-sm">
          Loading invoice adjustments...
        </div>
      ) : filteredListRows.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center font-semibold text-slate-500 shadow-sm">
          No invoice adjustments found.
        </div>
      ) : (
        filteredListRows.map((row, index) => {
          const rowDocNo = row.DocNo || row.docNo || row.Docno || "";
          const partyName = row.VName || row.Vname || row.vname || row.Customer || row.Supplier || "-";
          const mobile = row.Mobile || row.mobile || row.Vcode || row.vcode || row.VCode || "-";
          const date = row.DocDate || row.Date || row.InvDate || row.Actiontime || "";
          const invoiceValue = row.InvValule || row.invValule || row.InvValue || 0;
          const cashPayments = row.CashPayments || row.cashpayments || 0;
          const bankPayments = row.BankPayments || row.bankpayments || 0;
          const otherPayments = row.OtherPayments || row.otherPayments || 0;
          const balance = row.Balance || row.balance || 0;
          const adjAmount = row.AdjAmt || row.AdjAmount || 0;
          return (
            <div key={getRowKey(row, index)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="space-y-1 text-sm text-slate-900">
                {rowDocNo && (
                  <p>
                    <span className="font-bold">DocNo:</span> {rowDocNo}
                  </p>
                )}
                <p>
                  <span className="font-bold">Customer:</span> {partyName}
                </p>
                <p>
                  <span className="font-bold">Mobile:</span> {mobile}
                </p>
                {date && (
                  <p>
                    <span className="font-bold">Date:</span> {String(date).split("T")[0]}
                  </p>
                )}
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-slate-100 pt-3">
                  <p>
                    <span className="font-bold">Invoice:</span> {currency(invoiceValue)}
                  </p>
                  <p>
                    <span className="font-bold">Cash:</span> {currency(cashPayments)}
                  </p>
                  <p>
                    <span className="font-bold">Bank:</span> {currency(bankPayments)}
                  </p>
                  <p>
                    <span className="font-bold">Other:</span> {currency(otherPayments)}
                  </p>
                  <p>
                    <span className="font-bold">Balance:</span> {currency(balance)}
                  </p>
                  <p>
                    <span className="font-bold">Adj Amt:</span> {currency(adjAmount)}
                  </p>
                </div>
                <div className="pt-1">
                  <span className="inline-flex rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold uppercase text-teal-700">
                    {row.tag || row.Tag || "-"}
                  </span>
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  if (mode === "form") {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <Heading>{editingRow ? "Edit Invoice Adjustment" : "New Invoice Adjustment"}</Heading>

          <div className="mb-4 border-b border-slate-200 pb-2 text-sm font-medium text-slate-800">
            Adjustment Details
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">DocNo</label>
              <input
                type="text"
                value={docNo}
                readOnly
                tabIndex={-1}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">From Date</label>
              <input
                type="date"
                ref={fromDateRef}
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    toDateRef.current?.focus();
                  }
                }}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-teal-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">To Date</label>
              <input
                type="date"
                ref={toDateRef}
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    customerInputRef.current?.focus();
                    setCustomerOpen(true);
                  }
                }}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-teal-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Vendor Type</label>
              <select
                value={selectedVendorType}
                onChange={(event) => {
                  setSelectedVendorType(event.target.value);
                  setSelectedVid("");
                  setSelectedVids([]);
                  setCustomerQuery("");
                  setCustomerOpen(false);
                  setHighlightedCustomer(0);
                }}
                disabled={formLoading || vendorTypes.length === 0}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-teal-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              >
                {vendorTypes.map((type, index) => (
                  <option key={`${type.VType || index}-${index}`} value={type.VType}>
                    {type.VType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto] md:items-start">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Customer</label>
              <div className="relative w-full">
                <input
                  ref={customerInputRef}
                  type="text"
                  value={customerQuery}
                  onChange={(event) => {
                    setCustomerQuery(event.target.value);
                    setSelectedVid("");
                    setCustomerOpen(true);
                    setHighlightedCustomer(0);
                  }}
                  onFocus={() => setCustomerOpen(true)}
                  onBlur={() => {
                    window.setTimeout(() => setCustomerOpen(false), 150);
                  }}
                  onKeyDown={handleCustomerKeyDown}
                  disabled={formLoading}
                  placeholder="Search and select customer"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-9 text-sm text-slate-800 outline-none focus:border-teal-400"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    customerInputRef.current?.focus();
                    setCustomerOpen((value) => !value);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  aria-label="Show customers"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>

                {customerOpen && (
                  <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-2 text-slate-500">No customers found</div>
                    ) : (
                      filteredCustomers.map((customer, index) => {
                        const vid = String(getCustomerVid(customer));
                        const label = getCustomerLabel(customer);
                        const highlighted = index === highlightedCustomer;
                        const selected = selectedVids.includes(vid);

                        return (
                          <button
                            key={vid || index}
                            type="button"
                            onMouseEnter={() => setHighlightedCustomer(index)}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              toggleCustomer(customer);
                            }}
                            className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition ${
                              highlighted
                                ? "bg-teal-600 text-white"
                                : selected
                                  ? "bg-teal-50 text-teal-800"
                                  : "text-slate-700 hover:bg-teal-50"
                            }`}
                          >
                            <span>{label || vid}</span>
                            {selected && (
                              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${highlighted ? "bg-white/20 text-white" : "bg-teal-100 text-teal-700"}`}>
                                Selected
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <label className="mb-1 block text-sm font-medium text-slate-600">Mode</label>
              <button
                type="button"
                ref={modeButtonRef}
                onClick={() => {
                  const selectedIndex = paymentModes.findIndex(
                    (paymentMode) =>
                      String(getPaymentModeId(paymentMode)) === String(selectedBankId)
                  );
                  setHighlightedPaymentMode(Math.max(selectedIndex, 0));
                  setPaymentModeOpen((value) => !value);
                }}
                onKeyDown={handlePaymentModeKeyDown}
                disabled={formLoading || paymentModes.length === 0}
                className="flex h-10 w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-800 outline-none transition hover:border-teal-300 focus:border-teal-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              >
                <span className="truncate">
                  {getPaymentModeLabel(selectedPaymentMode) || "Select mode"}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
              </button>
              {paymentModeOpen && (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg">
                  {paymentModes.length === 0 ? (
                    <div className="px-3 py-2 text-slate-500">No modes found</div>
                  ) : (
                  paymentModes.map((paymentMode, index) => {
                    const bankId = String(getPaymentModeId(paymentMode));
                    const highlighted = index === highlightedPaymentMode;
                    const selected = bankId === String(selectedBankId);
                    return (
                      <button
                        key={`${bankId}-${index}`}
                        type="button"
                        onMouseEnter={() => setHighlightedPaymentMode(index)}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectPaymentMode(index);
                        }}
                        className={`block w-full px-3 py-2 text-left transition ${
                          highlighted
                            ? "bg-teal-600 font-semibold text-white"
                            : selected
                              ? "bg-teal-50 font-semibold text-teal-800"
                            : "text-slate-700 hover:bg-teal-50"
                        }`}
                      >
                        {getPaymentModeLabel(paymentMode) || bankId}
                      </button>
                    );
                  })
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              ref={refreshButtonRef}
              onClick={loadVendorAdjustment}
              disabled={formLoading || adjustmentLoading}
              className="mt-6 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
            >
              <RefreshCw className={`h-4 w-4 ${adjustmentLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Code</th>
                  <th className="px-4 py-3 text-left font-semibold">Customer</th>
                  <th className="px-4 py-3 text-left font-semibold">Mobile</th>
                  <th className="px-4 py-3 text-right font-semibold">Opening</th>
                  <th className="px-4 py-3 text-right font-semibold">Invoice</th>
                  <th className="px-4 py-3 text-right font-semibold">Cash</th>
                  <th className="px-4 py-3 text-right font-semibold">Bank</th>
                  <th className="px-4 py-3 text-right font-semibold">Other</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance</th>
                  <th className="px-4 py-3 text-right font-semibold">AdjAmount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {adjustmentLoading ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center font-semibold text-slate-500">
                      Loading customer invoice balance...
                    </td>
                  </tr>
                ) : adjustmentRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center font-semibold text-slate-500">
                      Select customer and refresh to view invoice adjustment rows.
                    </td>
                  </tr>
                ) : (
                  adjustmentRows.map((row, index) => {
                    const key = getRowKey(row, index);
                    return (
                      <tr key={key} className="hover:bg-teal-50/60">
                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-700">{row.vcode || row.VCode || row.Vcode || "-"}</td>
                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-900">{row.vname || row.VName || row.Vname || "-"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.mobile || row.Mobile || "-"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">{currency(row.opbalance || row.OpBalance)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">{currency(row.invValule || row.InvValule || row.InvValue)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">{currency(row.cashpayments || row.CashPayments)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">{currency(row.bankpayments || row.BankPayments)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">{currency(row.OtherPayments)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-rose-700">{currency(row.Balance || row.balance)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={adjAmounts[key] ?? ""}
                            onChange={(event) =>
                              setAdjAmounts((current) => ({
                                ...current,
                                [key]: event.target.value.replace(/[^0-9.]/g, ""),
                              }))
                            }
                            className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-right text-sm font-semibold text-slate-800 outline-none focus:border-teal-400"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={saveAdjustment}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            <Save className="h-3.5 w-3.5" />
            {editingRow ? "Update" : "Save"}
          </button>
          <button
            type="button"
            onClick={closeForm}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-700"
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-white">
        <div className="px-2 pt-3 sm:px-3">
          <Heading>Invoice Adjustment</Heading>
        </div>

        <div className="flex items-center justify-between border-b border-slate-200 px-2 pb-2 sm:px-3">
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700"
          >
            <Plus className="h-4 w-4" />
            New
          </button>

          <div className="inline-flex rounded-lg bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setViewMode("card")}
              className={`rounded-md p-2 transition ${viewMode === "card" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}
              title="Card view"
            >
              <Grid2X2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`rounded-md p-2 transition ${viewMode === "table" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}
              title="Table view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200 px-2 py-2 sm:px-3">
          <div className="relative w-full max-w-lg">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-teal-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search invoice adjustment..."
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pl-9 text-sm text-slate-800 outline-none focus:border-teal-400"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
          {error}
        </div>
      )}

      {viewMode === "table" ? renderExplorerTable() : renderExplorerCards()}
    </div>
  );
};

export default InvAdjustment;
